/**
 * Entity commands, owned by the World (review F-1): the only code that ever
 * receives a WorldWriter. Each definition validates its JSON payload against
 * the live world and the component schemas, then applies an immutable update
 * and returns the previous value as its undo memo.
 *
 * Coalescing follows architecture §4.2: commands carrying the same `session`
 * id (one gizmo drag, one Inspector field edit) merge into a single undo step,
 * keeping the first memo and the last payload.
 */
import { parsePayload } from "../commands/payload";
import type { CommandDefinition, Executed, ValidationResult } from "../commands/types";
import type { ManifestEntity } from "../assets/content";
import { entityId, fromPlainEntity, toPlainEntity } from "./entity";
import type { EntityId, EntityRecord, PlainEntity } from "./entity";
import type { Vec3 } from "../core/math/vec3";
import { isLevelRoot, subtreeOf } from "./hierarchy";
import { whyCantAdd, whyCantHave, whyCantRemove, whyCantToggle } from "./schema/componentRules";
import { LevelFormatError, prepareEntities } from "./serialization";
import type { PreparedEntities } from "./serialization";
import { localTransformAt } from "./localTransform";
import { worldMatrixOf } from "./TransformSystem";
import { invalidNameReason } from "./names";
import type { ComponentRegistry } from "./schema/ComponentRegistry";
import type { ComponentData } from "./schema/defineComponent";
import { validateProp } from "./schema/propTypes";
import type { WorldReader, WorldWriter } from "./World";

export interface EntityCommandDeps {
  readonly writer: WorldWriter;
  readonly reader: WorldReader;
  readonly components: ComponentRegistry;
}

export interface SetPropertyPayload {
  readonly entity: EntityId;
  readonly component: string;
  readonly prop: string;
  /** Targets one element of an array prop (e.g. a single highlight). */
  readonly index?: number;
  readonly value: unknown;
  readonly session?: string;
}

export interface SetTransformPayload {
  readonly entity: EntityId;
  readonly position: readonly [number, number, number];
  readonly rotation: readonly [number, number, number];
  readonly scale: readonly [number, number, number];
  readonly session?: string;
}

export function entityCommands(deps: EntityCommandDeps): CommandDefinition[] {
  return [
    setPropertyCommand(deps),
    setTransformCommand(deps),
    renameCommand(deps),
    setVisibleCommand(deps),
    deleteCommand(deps),
    createCommand(deps),
    moveCommand(deps),
    addComponentCommand(deps),
    removeComponentCommand(deps),
    setComponentEnabledCommand(deps),
  ] as CommandDefinition[];
}

// ─── entity.setProperty ──────────────────────────────────────────────────

function setPropertyCommand({
  writer,
  reader,
  components,
}: EntityCommandDeps): CommandDefinition<SetPropertyPayload, ComponentData> {
  return {
    type: "entity.setProperty",
    undoable: true,
    validate: (raw) => {
      const shape = parsePayload(raw, {
        entity: "string",
        component: "string",
        prop: "string",
        index: "number?",
        value: "unknown",
        session: "string?",
      });
      if (!shape.ok) return shape;
      return validateSetProperty(shape.value as SetPropertyPayload, reader, components);
    },
    do: (payload) => {
      const previous = requireComponent(reader, payload.entity, payload.component);
      writer.setComponent(payload.entity, payload.component, applyProp(previous, payload));
      return previous;
    },
    undo: (payload, previous) => {
      writer.setComponent(payload.entity, payload.component, previous);
    },
    merge: mergeBySession((p) => `${p.entity}/${p.component}/${p.prop}/${p.index ?? ""}`),
    describe: (payload) => `Set ${payload.component}.${payload.prop}`,
  };
}

function validateSetProperty(
  payload: SetPropertyPayload,
  reader: WorldReader,
  components: ComponentRegistry,
): ValidationResult<SetPropertyPayload> {
  const entity = reader.get(payload.entity);
  if (!entity) return fail(`No entity '${payload.entity}'`);

  const current = entity.components.get(payload.component);
  if (!current) return fail(`${entity.name} has no ${payload.component} component`);

  const propType = components.get(payload.component)?.props[payload.prop];
  if (!propType) return fail(`${payload.component} has no property '${payload.prop}'`);

  const label = `${payload.component}.${payload.prop}`;
  let targetType = propType;
  if (payload.index !== undefined) {
    const list = current[payload.prop];
    if (propType.type !== "array" || !Array.isArray(list)) return fail(`${label} is not a list`);
    if (!Number.isInteger(payload.index) || payload.index < 0 || payload.index >= list.length) {
      return fail(`${label} has no index ${payload.index}`);
    }
    targetType = propType.of;
  }

  const value = validateProp(targetType, payload.value);
  if (!value.ok) return fail(`${label}: ${value.error}`);

  return { ok: true, value: { ...payload, value: value.value } };
}

/** Returns new component data with one prop (or one array element) replaced. */
function applyProp(data: ComponentData, payload: SetPropertyPayload): ComponentData {
  if (payload.index === undefined) return Object.freeze({ ...data, [payload.prop]: payload.value });

  const list = [...(data[payload.prop] as readonly unknown[])];
  list[payload.index] = payload.value;
  return Object.freeze({ ...data, [payload.prop]: list });
}

// ─── entity.setTransform ─────────────────────────────────────────────────

function setTransformCommand({
  writer,
  reader,
  components,
}: EntityCommandDeps): CommandDefinition<SetTransformPayload, ComponentData> {
  return {
    type: "entity.setTransform",
    undoable: true,
    validate: (raw) => {
      const shape = parsePayload(raw, {
        entity: "string",
        position: "unknown",
        rotation: "unknown",
        scale: "unknown",
        session: "string?",
      });
      if (!shape.ok) return shape;

      const entity = reader.get(shape.value.entity as EntityId);
      if (!entity) return fail(`No entity '${shape.value.entity}'`);
      if (!entity.components.has("Transform"))
        return fail(`${entity.name} has no Transform component`);

      for (const prop of ["position", "rotation", "scale"] as const) {
        const result = components.validateProp("Transform", prop, shape.value[prop]);
        if (!result.ok) return fail(`Transform.${prop}: ${result.error}`);
      }
      return { ok: true, value: shape.value as SetTransformPayload };
    },
    do: (payload) => {
      const previous = requireComponent(reader, payload.entity, "Transform");
      writer.setComponent(
        payload.entity,
        "Transform",
        Object.freeze({
          position: payload.position,
          rotation: payload.rotation,
          scale: payload.scale,
        }),
      );
      return previous;
    },
    undo: (payload, previous) => {
      writer.setComponent(payload.entity, "Transform", previous);
    },
    merge: mergeBySession((p) => p.entity),
    describe: (payload) => `Transform ${nameOf(reader, payload.entity)}`,
  };
}

// ─── entity.rename / entity.setVisible ───────────────────────────────────

function renameCommand({
  writer,
  reader,
}: EntityCommandDeps): CommandDefinition<{ entity: EntityId; name: string }, string> {
  return {
    type: "entity.rename",
    undoable: true,
    validate: (raw) => {
      const shape = parsePayload(raw, { entity: "string", name: "string" });
      if (!shape.ok) return shape;

      const entity = reader.get(shape.value.entity as EntityId);
      const name = shape.value.name.trim();
      if (!entity) return fail(`No entity '${shape.value.entity}'`);
      const invalid = invalidNameReason(name);
      if (invalid) return fail(invalid);

      // Paths stay unique: a name may repeat elsewhere, but not among siblings.
      if (siblingsOf(reader, entity).some((sibling) => sibling.name === name))
        return fail(`'${name}' is already the name of a sibling`);
      return { ok: true, value: { entity: entity.id, name } };
    },
    do: (payload) => {
      const previous = nameOf(reader, payload.entity);
      writer.rename(payload.entity, payload.name);
      return previous;
    },
    undo: (payload, previous) => {
      writer.rename(payload.entity, previous);
    },
    // describe runs before do, so the entity still has its old name here.
    describe: (payload) => `Rename ${nameOf(reader, payload.entity)} to ${payload.name}`,
  };
}

function setVisibleCommand({
  writer,
  reader,
}: EntityCommandDeps): CommandDefinition<{ entity: EntityId; visible: boolean }, boolean> {
  return {
    type: "entity.setVisible",
    undoable: true,
    validate: (raw) => {
      const shape = parsePayload(raw, { entity: "string", visible: "boolean" });
      if (!shape.ok) return shape;
      if (!reader.get(shape.value.entity as EntityId))
        return fail(`No entity '${shape.value.entity}'`);
      return {
        ok: true,
        value: { entity: shape.value.entity as EntityId, visible: shape.value.visible },
      };
    },
    do: (payload) => {
      const previous = requireEntity(reader, payload.entity).visible;
      writer.setVisible(payload.entity, payload.visible);
      return previous;
    },
    undo: (payload, previous) => {
      writer.setVisible(payload.entity, previous);
    },
    describe: (payload) => `${payload.visible ? "Show" : "Hide"} ${nameOf(reader, payload.entity)}`,
  };
}

// ─── entity.delete ───────────────────────────────────────────────────────

/** What undo needs to put a deleted subtree back exactly: its records and its place. */
interface DeleteMemo {
  readonly parent: string | null;
  readonly index: number;
  readonly entities: readonly PlainEntity[];
}

function deleteCommand({
  writer,
  reader,
}: EntityCommandDeps): CommandDefinition<{ entity: EntityId }, DeleteMemo> {
  return {
    type: "entity.delete",
    undoable: true,
    validate: (raw) => {
      const shape = parsePayload(raw, { entity: "string" });
      if (!shape.ok) return shape;
      const entity = reader.get(shape.value.entity as EntityId);
      if (!entity) return fail(`No entity '${shape.value.entity}'`);
      if (isLevelRoot(entity)) return fail(`${entity.name} is the level root and can't be deleted`);
      return { ok: true, value: { entity: entity.id } };
    },
    do: ({ entity }) => {
      const snapshot = reader.getSnapshot();
      const record = requireEntity(reader, entity);
      const siblings =
        record.parent === null ? snapshot.roots : requireEntity(reader, record.parent).children;
      const memo: DeleteMemo = {
        parent: record.parent,
        index: siblings.indexOf(entity),
        entities: subtreeOf(snapshot.entities, entity).map((id) =>
          toPlainEntity(requireEntity(reader, id)),
        ),
      };
      writer.remove(entity);
      return memo;
    },
    undo: (_payload, memo) => {
      const parent = memo.parent === null ? null : (memo.parent as EntityId);
      writer.insert(memo.entities.map(fromPlainEntity), parent, memo.index);
    },
    describe: ({ entity }) => `Delete ${nameOf(reader, entity)}`,
  };
}

// ─── entity.create ───────────────────────────────────────────────────────

export interface CreatePayload {
  /** The new entity's parent, or null for the top level. */
  readonly parent: EntityId | null;
  /** The sibling it goes in front of; last when absent, as Unreal's Outliner and Unity's Hierarchy add. */
  readonly before?: EntityId;
  /**
   * The new entity and its children, as a level manifest writes them: one
   * shape for creating, duplicating and pasting. Ids are the caller's, so the
   * request replays to the same entities; missing props take schema defaults.
   */
  readonly entity: ManifestEntity;
}

function createCommand({
  writer,
  reader,
  components,
}: EntityCommandDeps): CommandDefinition<CreatePayload, undefined> {
  return {
    type: "entity.create",
    undoable: true,
    validate: (raw) => {
      const shape = parsePayload(raw, { parent: "unknown", before: "string?", entity: "unknown" });
      if (!shape.ok) return shape;
      return validateCreate(shape.value, reader, components);
    },
    do: ({ parent, before, entity }) => {
      // Validated, so this can't throw or warn now.
      const { records } = prepareEntities(
        [entity],
        parent,
        components,
        reader.getSnapshot().entities,
      );
      const siblings = childIds(reader, parent);
      writer.insert(
        records,
        parent,
        before === undefined ? siblings.length : siblings.indexOf(before),
      );
      return undefined;
    },
    undo: ({ entity }) => {
      writer.remove(entityId(entity.id));
    },
    describe: ({ entity }) => `Create ${entity.name}`,
  };
}

function validateCreate(
  raw: { parent: unknown; before?: string; entity: unknown },
  reader: WorldReader,
  registry: ComponentRegistry,
): ValidationResult<CreatePayload> {
  if (raw.parent !== null && typeof raw.parent !== "string")
    return fail("parent must be an entity id or null");
  const parent = raw.parent === null ? null : (raw.parent as EntityId);
  if (parent !== null && !reader.get(parent)) return fail(`No entity '${parent}'`);

  const before = raw.before as EntityId | undefined;
  if (before !== undefined && !childIds(reader, parent).includes(before))
    return fail(`'${before}' isn't a child of the parent`);

  const tree = parseEntityTree(raw.entity);
  if (!tree.ok) return tree;
  const entity = tree.value;

  const placed = placeSubtree(entity, parent, reader, registry);
  if (placed) return fail(placed);

  return {
    ok: true,
    value: { parent, ...(before !== undefined && { before }), entity },
  };
}

/**
 * Why a subtree can't go under `parent`, or null if it can. Everything a level
 * load would only warn about (a bad value, a dangling reference) refuses here:
 * a request should create exactly what it says.
 */
function placeSubtree(
  entity: ManifestEntity,
  parent: EntityId | null,
  reader: WorldReader,
  registry: ComponentRegistry,
): string | null {
  const snapshot = reader.getSnapshot();
  let prepared: PreparedEntities;
  try {
    prepared = prepareEntities([entity], parent, registry, snapshot.entities);
  } catch (error) {
    if (error instanceof LevelFormatError) return error.message;
    throw error;
  }
  const [warning] = prepared.warnings;
  if (warning !== undefined) return warning;

  const taken = prepared.records.find((record) => snapshot.entities.has(record.id));
  if (taken) return `Entity id '${taken.id}' is already taken`;

  if (childrenOf(reader, parent).some((sibling) => sibling.name === entity.name))
    return `'${entity.name}' is already the name of a sibling`;

  // The component rules, checked as if the subtree were already in the level.
  const after = new Map(snapshot.entities);
  for (const record of prepared.records) after.set(record.id, record);
  for (const record of prepared.records) {
    const reason = whyCantHave(registry, after, record.id);
    if (reason) return reason;
  }
  return null;
}

/**
 * A manifest entity tree from untyped JSON (a request, a pasted clipboard),
 * canonical: unknown keys dropped, names trimmed.
 */
function parseEntityTree(raw: unknown): ValidationResult<ManifestEntity> {
  const shape = parsePayload(raw, {
    id: "string",
    name: "string",
    class: "string?",
    visible: "boolean?",
    components: "unknown",
    disabled: "string[]?",
    children: "unknown",
  });
  if (!shape.ok) return shape;
  const { components, children, ...rest } = shape.value;

  if (components !== undefined && !isPlainObject(components))
    return fail("components must be an object of component data by type");
  if (children !== undefined && !Array.isArray(children))
    return fail("children must be a list of entities");

  const parsedChildren: ManifestEntity[] = [];
  for (const child of (children ?? []) as unknown[]) {
    const parsed = parseEntityTree(child);
    if (!parsed.ok) return parsed;
    parsedChildren.push(parsed.value);
  }

  return {
    ok: true,
    value: {
      ...rest,
      name: rest.name.trim(),
      ...(components !== undefined && { components }),
      ...(parsedChildren.length > 0 && { children: parsedChildren }),
    },
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// ─── entity.move ─────────────────────────────────────────────────────────

export interface MovePayload {
  readonly entity: EntityId;
  /** The new parent, or null for the top level. */
  readonly parent: EntityId | null;
  /** The new sibling it goes in front of; at the end when absent. */
  readonly before?: EntityId;
}

/** Where a moved entity was, and its local transform there. */
interface MoveMemo {
  readonly parent: string | null;
  readonly index: number;
  readonly transform: ComponentData | null;
}

function moveCommand({
  writer,
  reader,
}: EntityCommandDeps): CommandDefinition<MovePayload, MoveMemo> {
  return {
    type: "entity.move",
    undoable: true,
    validate: (raw) => {
      const shape = parsePayload(raw, { entity: "string", parent: "unknown", before: "string?" });
      if (!shape.ok) return shape;
      return validateMove(shape.value, reader);
    },
    do: ({ entity, parent, before }) => {
      const record = requireEntity(reader, entity);
      const memo: MoveMemo = {
        parent: record.parent,
        index: childIds(reader, record.parent).indexOf(entity),
        transform: record.components.get("Transform") ?? null,
      };

      // Computed before the move, while the world matrix is still the old parent's.
      const transform = memo.transform && keepWorldPlace(reader, entity, parent, memo.transform);
      const siblings = childIds(reader, parent).filter((id) => id !== entity);
      writer.move(
        entity,
        parent,
        before === undefined ? siblings.length : siblings.indexOf(before),
      );
      if (transform) writer.setComponent(entity, "Transform", transform);
      return memo;
    },
    undo: ({ entity }, memo) => {
      writer.move(entity, memo.parent as EntityId | null, memo.index);
      if (memo.transform) writer.setComponent(entity, "Transform", memo.transform);
    },
    describe: ({ entity }) => `Move ${nameOf(reader, entity)}`,
  };
}

function validateMove(
  raw: { entity: string; parent: unknown; before?: string },
  reader: WorldReader,
): ValidationResult<MovePayload> {
  const entity = reader.get(raw.entity as EntityId);
  if (!entity) return fail(`No entity '${raw.entity}'`);
  if (isLevelRoot(entity)) return fail(`${entity.name} is the level root and stays where it is`);

  if (raw.parent !== null && typeof raw.parent !== "string")
    return fail("parent must be an entity id or null");
  const parent = raw.parent === null ? null : (raw.parent as EntityId);
  if (parent !== null && !reader.get(parent)) return fail(`No entity '${parent}'`);
  if (parent !== null && subtreeOf(reader.getSnapshot().entities, entity.id).includes(parent))
    return fail(`${entity.name} can't move inside itself`);

  const siblings = childrenOf(reader, parent).filter((sibling) => sibling.id !== entity.id);
  if (siblings.some((sibling) => sibling.name === entity.name))
    return fail(`'${entity.name}' is already the name of a sibling there`);

  const before = raw.before as EntityId | undefined;
  if (before !== undefined && !siblings.some((sibling) => sibling.id === before))
    return fail(`'${before}' isn't a child of the new parent`);

  return {
    ok: true,
    value: { entity: entity.id, parent, ...(before !== undefined && { before }) },
  };
}

/**
 * The local transform that keeps an entity where it is in the world under a
 * new parent, as Unreal's attach does by default: new parent's world inverse ×
 * the entity's world. Rotation stays continuous with the old Euler angles.
 */
function keepWorldPlace(
  reader: WorldReader,
  entity: EntityId,
  parent: EntityId | null,
  transform: ComponentData,
): ComponentData {
  const parentWorld = parent === null ? null : worldMatrixOf(reader, parent);
  const local = localTransformAt(
    worldMatrixOf(reader, entity),
    parentWorld,
    transform.rotation as Vec3,
  );
  return Object.freeze({ ...local });
}

// ─── entity.addComponent / entity.removeComponent ────────────────────────

interface ComponentPayload {
  readonly entity: EntityId;
  readonly component: string;
}

/** Adds a component with its schema defaults (Unreal's Add Component, Unity's Add Component). */
function addComponentCommand({
  writer,
  reader,
  components,
}: EntityCommandDeps): CommandDefinition<ComponentPayload, undefined> {
  return {
    type: "entity.addComponent",
    undoable: true,
    validate: (raw) => {
      const shape = parsePayload(raw, { entity: "string", component: "string" });
      if (!shape.ok) return shape;
      const payload = { entity: shape.value.entity as EntityId, component: shape.value.component };
      const reason = whyCantAdd(
        components,
        reader.getSnapshot().entities,
        payload.entity,
        payload.component,
      );
      return reason ? fail(reason) : { ok: true, value: payload };
    },
    do: ({ entity, component }) => {
      writer.setComponent(entity, component, components.parse(component, {}).data);
      return undefined;
    },
    undo: ({ entity, component }) => {
      writer.removeComponent(entity, component);
    },
    describe: ({ entity, component }) => `Add ${component} to ${nameOf(reader, entity)}`,
  };
}

/** Removes a component; its data is the memo, so undo restores it exactly. */
function removeComponentCommand({
  writer,
  reader,
  components,
}: EntityCommandDeps): CommandDefinition<ComponentPayload, ComponentData> {
  return {
    type: "entity.removeComponent",
    undoable: true,
    validate: (raw) => {
      const shape = parsePayload(raw, { entity: "string", component: "string" });
      if (!shape.ok) return shape;
      const payload = { entity: shape.value.entity as EntityId, component: shape.value.component };
      const reason = whyCantRemove(
        components,
        reader.getSnapshot().entities,
        payload.entity,
        payload.component,
      );
      return reason ? fail(reason) : { ok: true, value: payload };
    },
    do: ({ entity, component }) => {
      const previous = requireComponent(reader, entity, component);
      writer.removeComponent(entity, component);
      return previous;
    },
    undo: ({ entity, component }, previous) => {
      writer.setComponent(entity, component, previous);
    },
    describe: ({ entity, component }) => `Remove ${component} from ${nameOf(reader, entity)}`,
  };
}

/** Turns a component off or on (Unity's enabled checkbox); the memo is its previous state. */
function setComponentEnabledCommand({
  writer,
  reader,
  components,
}: EntityCommandDeps): CommandDefinition<ComponentPayload & { enabled: boolean }, boolean> {
  return {
    type: "entity.setComponentEnabled",
    undoable: true,
    validate: (raw) => {
      const shape = parsePayload(raw, {
        entity: "string",
        component: "string",
        enabled: "boolean",
      });
      if (!shape.ok) return shape;
      const payload = {
        entity: shape.value.entity as EntityId,
        component: shape.value.component,
        enabled: shape.value.enabled,
      };
      const reason = whyCantToggle(
        components,
        reader.getSnapshot().entities,
        payload.entity,
        payload.component,
      );
      return reason ? fail(reason) : { ok: true, value: payload };
    },
    do: ({ entity, component, enabled }) => {
      const wasEnabled = requireEntity(reader, entity).disabled?.has(component) !== true;
      writer.setComponentEnabled(entity, component, enabled);
      return wasEnabled;
    },
    undo: ({ entity, component }, wasEnabled) => {
      writer.setComponentEnabled(entity, component, wasEnabled);
    },
    describe: ({ entity, component, enabled }) =>
      `${enabled ? "Enable" : "Disable"} ${component} on ${nameOf(reader, entity)}`,
  };
}

// ─── Shared helpers ──────────────────────────────────────────────────────

/**
 * Merges consecutive executions that share a `session` id and target the same
 * thing (`keyOf`): first memo, last payload (architecture §4.2).
 */
function mergeBySession<P extends { readonly session?: string }, M>(keyOf: (payload: P) => string) {
  return (previous: Executed<P, M>, next: Executed<P, M>): Executed<P, M> | null => {
    const session = previous.payload.session;
    if (session === undefined || session !== next.payload.session) return null;
    if (keyOf(previous.payload) !== keyOf(next.payload)) return null;
    return { payload: next.payload, memo: previous.memo };
  };
}

function requireEntity(reader: WorldReader, id: EntityId): EntityRecord {
  const entity = reader.get(id);
  if (!entity) throw new Error(`No entity '${id}'`);
  return entity;
}

function requireComponent(reader: WorldReader, id: EntityId, type: string): ComponentData {
  const data = requireEntity(reader, id).components.get(type);
  if (!data) throw new Error(`Entity '${id}' has no ${type} component`);
  return data;
}

function nameOf(reader: WorldReader, id: EntityId): string {
  return reader.get(id)?.name ?? id;
}

function fail(error: string): { ok: false; error: string } {
  return { ok: false, error };
}

/** The other entities under the same parent (or the other roots). */
function siblingsOf(reader: WorldReader, entity: EntityRecord): EntityRecord[] {
  return childrenOf(reader, entity.parent).filter((sibling) => sibling.id !== entity.id);
}

/** A parent's child ids, or the roots for null. */
function childIds(reader: WorldReader, parent: EntityId | null): readonly EntityId[] {
  const snapshot = reader.getSnapshot();
  return parent === null ? snapshot.roots : (snapshot.entities.get(parent)?.children ?? []);
}

/** A parent's children, or the roots for null. */
function childrenOf(reader: WorldReader, parent: EntityId | null): EntityRecord[] {
  const snapshot = reader.getSnapshot();
  return childIds(reader, parent)
    .map((id) => snapshot.entities.get(id))
    .filter((child): child is EntityRecord => child !== undefined);
}
